import {
	Button,
	Drawer,
	Header,
	IconChevronLeft,
	LogoBadgetAC,
	LogoBadgetCP,
	LogoPrimaryAC,
	LogoPrimaryCP,
	NavbarVertical,
	NotificationsBar,
	ProfileNav,
	Text,
} from "@consumidor-positivo/aurora";
import { useLocation } from "react-router-dom";

import { pageNames } from "@shared/routes/pageNames";
import { getBrand } from "@shared/services/brandConfig";
import { useLoggedInHeader } from "./hook";
import { notifyIcons } from "./icons";
import "./styles.scss";

const homePaths = [
	pageNames.myDebts.path,
	pageNames.myAgreements.path,
	pageNames.myScore.path,
];

const brand = getBrand();
const LogoPrimary = brand === "ac" ? LogoPrimaryAC : LogoPrimaryCP;
const LogoBadget = brand === "ac" ? LogoBadgetAC : LogoBadgetCP;

interface LoggedInHeaderProps {
	username?: string;
}

export const LoggedInHeader: React.FC<LoggedInHeaderProps> = ({ username }) => {
	const location = useLocation();
	const isHome = homePaths.includes(location.pathname);

	const {
		dataSourceNavbar,
		dataSourceNavbarVertical,
		drawerOpen,
		news,
		olds,
		notifications,
		formatDate,
		firstName,
		handleOpenDrawer,
		handleNavigateNotification,
		deleteNotification,
		handleLogoClick,
		handleSignOut,
	} = useLoggedInHeader();

	const renderNotificationList = (
		title: string,
		items: typeof notifications,
	) => (
		<NotificationsBar.List
			title={title}
			dataSource={items.map((item) => ({
				...item,
				createdAt: (item as any).date || "",
				onClick: () => handleNavigateNotification(item),
			}))}
			renderItem={(item) => (
				<NotificationsBar.Link
					key={`notification-item-${item.title}`}
					title={item.title}
					Icon={notifyIcons[(item as any).category as keyof typeof notifyIcons]}
					createdAt={formatDate(item.createdAt || "")}
					onDelete={() => deleteNotification(item as any)}
					onClick={() => {
						handleOpenDrawer("notification");
						item.onClick?.();
					}}
				/>
			)}
		/>
	);

	return (
		<>
			<div className="logged-in-header">
				<Header.Root>
					<Header.Button onClick={() => handleLogoClick(isHome)}>
						<Header.Logo
							renderDesktop={<LogoPrimary />}
							renderMobile={
								<div className="logged-in-header__logo-mobile">
									{!isHome && <IconChevronLeft />}
									<LogoBadget />
								</div>
							}
						/>
					</Header.Button>
					<Header.Navigation>
						<Header.Navbar
							data={dataSourceNavbar}
							renderItem={(item) => (
								<Header.NavbarLink
									key={item.name}
									active={item.active}
									name={item.name}
									dropdown={item.dropdown}
									onClick={item.onClick}
								/>
							)}
						/>
						<Header.Actions divider>
							<Header.Profile
								onClickMenu={() => handleOpenDrawer("menu")}
								fullName={username || ""}
								notifications={{
									visible: true,
									count: news.length,
									hasUnread: news.length > 0,
									onClick: () => handleOpenDrawer("notification"),
								}}
							/>
						</Header.Actions>
					</Header.Navigation>

					<Drawer
						isOpen={drawerOpen.menu}
						handleOpen={() => handleOpenDrawer("menu")}
						renderHeader={
							<ProfileNav
								name={firstName(username || "")}
								fullName={username || ""}
							/>
						}
						renderContent={
							<NavbarVertical
								data={dataSourceNavbarVertical}
								renderItem={(link) => {
									const dropdown = link.dropdown?.map((item) => ({
										...item,
										onClick: () => {
											handleOpenDrawer("menu");
											item.onClick?.();
										},
									}));

									return (
										<NavbarVertical.Link
											key={link.name}
											active={link.active}
											Icon={link.Icon}
											name={link.name}
											dropdown={dropdown}
											onClick={() => {
												handleOpenDrawer("menu");
												link.onClick?.();
											}}
										/>
									);
								}}
								renderActions={() => (
									<div className="logged-in-header__actions">
										<Button type="outlined" expand="x" onClick={handleSignOut}>
											Sair
										</Button>
									</div>
								)}
							/>
						}
					/>

					<Drawer
						isOpen={drawerOpen.notification}
						handleOpen={() => handleOpenDrawer("notification")}
						renderHeader={
							<Text as="h3" variant="heading-small" weight="bold">
								Notificações
							</Text>
						}
						renderContent={
							<NotificationsBar.Root
								renderRecents={() =>
									renderNotificationList("Recentes", news as any)
								}
								renderOlds={() =>
									renderNotificationList("Antigas", olds as any)
								}
							/>
						}
					/>
				</Header.Root>
			</div>
		</>
	);
};
